import { useCallback, useState } from "react";

import {
  WorkspacesService,
  type CreateWorkspaceRequest,
  type Workspace,
} from "@/api";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import {
  isValidWorkspaceSlug,
  slugifyWorkspaceName,
} from "@/lib/workspace-helpers";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (workspace: Workspace) => void | Promise<void>;
}

/**
 * Create-workspace modal. Slug auto-derives from the name (the user
 * can override by editing the slug field directly — once they
 * touch it the auto-derivation pauses). Backend enforces tenant
 * uniqueness on ``(tenant_id, slug)`` + the 100-workspace cap.
 */
export function AddWorkspaceModal({ open, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugDirty, setSlugDirty] = useState(false);
  const [description, setDescription] = useState("");

  const mutator = useCallback(
    (payload: CreateWorkspaceRequest) => WorkspacesService.create(payload),
    []
  );
  const { mutate, isLoading, error, reset } = useMutation<
    CreateWorkspaceRequest,
    Workspace
  >(mutator, {
    onSuccess: async (ws) => {
      await onCreated(ws);
      // Form reset done by handleClose path; success path leaves
      // the parent to decide when to close.
      setName("");
      setSlug("");
      setSlugDirty(false);
      setDescription("");
    },
  });

  const handleNameChange = (value: string) => {
    setName(value);
    if (!slugDirty) {
      setSlug(slugifyWorkspaceName(value));
    }
  };

  const handleSlugChange = (value: string) => {
    setSlugDirty(true);
    setSlug(value);
  };

  const trimmedName = name.trim();
  const trimmedSlug = slug.trim();
  const trimmedDesc = description.trim();

  const nameValid = trimmedName.length >= 1 && trimmedName.length <= 200;
  const slugValid = isValidWorkspaceSlug(trimmedSlug);

  const canSubmit = nameValid && slugValid && !isLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const payload: CreateWorkspaceRequest = {
      name: trimmedName,
      slug: trimmedSlug,
    };
    if (trimmedDesc) payload.description = trimmedDesc;
    await mutate(payload);
  };

  const handleClose = () => {
    reset();
    setName("");
    setSlug("");
    setSlugDirty(false);
    setDescription("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} size="max-w-lg">
      <DialogHeader>
        <DialogTitle>{t("workspaces.addModal.title")}</DialogTitle>
        <DialogDescription>
          {t("workspaces.addModal.description")}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <FormField
            label={t("workspaces.addModal.nameLabel")}
            value={name}
            onChange={handleNameChange}
            placeholder={t("workspaces.addModal.namePlaceholder")}
            maxLength={200}
            required
            autoFocus
            error={
              name.length > 0 && !nameValid
                ? t("workspaces.addModal.nameError")
                : undefined
            }
          />

          <FormField
            label={t("workspaces.addModal.slugLabel")}
            value={slug}
            onChange={handleSlugChange}
            placeholder={t("workspaces.addModal.slugPlaceholder")}
            maxLength={100}
            required
            error={
              slug.length > 0 && !slugValid
                ? t("workspaces.addModal.slugError")
                : undefined
            }
            hint={t("workspaces.addModal.slugHint")}
          />

          <FormField
            label={t("workspaces.addModal.descriptionLabel")}
            value={description}
            onChange={setDescription}
            placeholder={t("workspaces.addModal.descriptionPlaceholder")}
            maxLength={2000}
          />

          {error && <Alert variant="danger">{parseApiError(error)}</Alert>}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("workspaces.addModal.cancel")}
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {isLoading
              ? t("workspaces.addModal.submitLoading")
              : t("workspaces.addModal.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
